import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import PeriodNavigator from "@/components/PeriodNavigator";
import EntriesTable from "@/components/EntriesTable";
import AdminCommissionsCard from "@/components/AdminCommissionsCard";
import AdminAttentionStrip from "@/components/AdminAttentionStrip";
import StatusBadge, { entryStatus } from "@/components/StatusBadge";
import { api, downloadFile } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { todayISO, formatHours, formatFriendlyDate, shiftISO } from "@/lib/dateUtils";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Loader2,
  Timer,
  Users,
  FileText,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Crown,
  Download,
  FolderOpen,
  Check,
  RotateCcw,
  Eye,
  DollarSign,
} from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");
  const [refDate, setRefDate] = useState(todayISO());
  const [customStart, setCustomStart] = useState(shiftISO(todayISO(), -6));
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [userFilter, setUserFilter] = useState(""); // user_id or "" for all
  const [projectFilter, setProjectFilter] = useState(""); // project_id or "" for all
  const [statusFilter, setStatusFilter] = useState(""); // draft/submitted/approved or "" for all
  const [entriesPage, setEntriesPage] = useState(1);

  const [totals, setTotals] = useState(null);
  const [projectTotals, setProjectTotals] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [entriesData, setEntriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  // Day/week/month send a reference date; custom sends explicit bounds.
  const customRangeValid =
    period !== "custom" ||
    (!!customStart && !!customEnd && customStart <= customEnd);
  const rangeParams = useMemo(
    () =>
      period === "custom"
        ? { period, start_date: customStart, end_date: customEnd }
        : { period, date: refDate },
    [period, refDate, customStart, customEnd]
  );

  const handleApprove = async (entry) => {
    try {
      await api.post(`/admin/entries/${entry.id}/approve`);
      toast.success("Entry approved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve entry.");
    }
  };

  const handleReturn = async (entry) => {
    try {
      await api.post(`/admin/entries/${entry.id}/return`);
      toast.success("Entry returned to draft");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not return entry.");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...rangeParams };
      if (userFilter) params.user_id = userFilter;
      if (projectFilter) params.project_id = projectFilter;
      if (statusFilter) params.status = statusFilter;
      const filename = await downloadFile("/admin/entries/export", params);
      toast.success("Export ready", {
        description: `Downloaded ${filename}`,
      });
    } catch (err) {
      toast.error("Export failed", {
        description: err?.message || "Could not generate CSV.",
      });
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    if (!customRangeValid) {
      // Wait for a complete, ordered custom range before hitting the API.
      return;
    }
    setLoading(true);
    setError("");
    try {
      const entriesParams = { ...rangeParams, page: entriesPage, page_size: 50 };
      if (userFilter) entriesParams.user_id = userFilter;
      if (projectFilter) entriesParams.project_id = projectFilter;
      if (statusFilter) entriesParams.status = statusFilter;

      const [tRes, pRes, eRes] = await Promise.all([
        api.get("/admin/totals", { params: rangeParams }),
        api.get("/admin/totals/projects", { params: rangeParams }),
        api.get("/admin/entries", { params: entriesParams }),
      ]);
      setTotals(tRes.data);
      setProjectTotals(pRes.data);
      setEntriesData(eRes.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load admin data.");
    } finally {
      setLoading(false);
    }
  }, [rangeParams, customRangeValid, userFilter, projectFilter, statusFilter, entriesPage]);

  useEffect(() => {
    setEntriesPage(1);
  }, [period, refDate, customStart, customEnd, userFilter, projectFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Load the active project list once for the filter dropdown so it shows
  // all projects, including those without hours yet in the current period.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/projects")
      .then(({ data }) => {
        if (!cancelled) setAllProjects(data.projects || []);
      })
      .catch(() => {
        if (!cancelled) setAllProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const freelancerOptions = useMemo(() => totals?.rows || [], [totals]);
  const grandTotal = totals?.grand_total_hours || 0;
  const grandCount = totals?.grand_entry_count || 0;
  const grandCost = totals?.grand_cost || 0;
  const activeCount = totals?.active_freelancers || 0;
  const freelancerCount = totals?.freelancer_count || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8"
        data-testid="admin-dashboard"
      >
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Admin Dashboard</h2>
            <p className="text-sm text-slate-500 mt-1">
              Signed in as <span className="font-medium">{user?.name}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={
                exporting ||
                !customRangeValid ||
                (entriesData?.entry_count || 0) === 0
              }
              data-testid="export-csv-button"
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                !customRangeValid
                  ? "Pick a valid start and end date to export"
                  : (entriesData?.entry_count || 0) === 0
                  ? "No entries to export in the current selection"
                  : "Download CSV for current filters"
              }
            >
              {exporting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Export CSV
            </button>
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ background: `${BRAND.purple}15`, color: BRAND.purple }}
            >
              <ShieldCheck size={14} />
              Admin
            </span>
          </div>
        </div>

        {/* Glanceable "what needs my review" strip */}
        <AdminAttentionStrip
          onEntriesPending={() => setStatusFilter("submitted")}
        />

        {/* Period + filter */}
        <PeriodNavigator
          period={period}
          setPeriod={setPeriod}
          refDate={refDate}
          setRefDate={setRefDate}
          customStart={customStart}
          setCustomStart={setCustomStart}
          customEnd={customEnd}
          setCustomEnd={setCustomEnd}
        />

        {/* Summary cards */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            className="rounded-2xl p-5 text-white"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-wider font-semibold">
              <Timer size={14} />
              Total hours
            </div>
            <div className="mt-1 text-3xl font-bold tabular-nums" data-testid="admin-grand-total">
              {formatHours(grandTotal)}
            </div>
            <div className="mt-1 text-sm text-white/90">across all freelancers</div>
          </div>
          <div className="rounded-2xl p-5 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
              <Users size={14} />
              Active freelancers
            </div>
            <div className="mt-1 text-3xl font-bold text-slate-900 tabular-nums" data-testid="admin-active-count">
              {activeCount}
              <span className="text-base font-medium text-slate-400 ml-1">
                / {freelancerCount}
              </span>
            </div>
            <div className="mt-1 text-sm text-slate-500">logged time this period</div>
          </div>
          <div className="rounded-2xl p-5 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
              <DollarSign size={14} />
              Total cost
            </div>
            <div className="mt-1 text-3xl font-bold text-slate-900 tabular-nums" data-testid="admin-grand-cost">
              ${Number(grandCost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {grandCost > 0
                ? "from freelancers with rates set"
                : "set rates on the Team page"}
            </div>
          </div>
          <div className="rounded-2xl p-5 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
              <FileText size={14} />
              Entries
            </div>
            <div className="mt-1 text-3xl font-bold text-slate-900 tabular-nums" data-testid="admin-entry-count">
              {grandCount}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {totals?.start_date}
              {totals?.start_date !== totals?.end_date ? ` – ${totals?.end_date}` : ""}
            </div>
          </div>
        </div>

        {/* Per-freelancer totals */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              By freelancer
            </h3>
          </div>
          <div
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
            data-testid="admin-totals-table"
          >
            {loading && !totals ? (
              <div className="p-10 flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-400" size={20} />
              </div>
            ) : freelancerOptions.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">
                No freelancers yet. Add one from the Team page (coming in Phase 4).
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left font-semibold px-4 py-3">Freelancer</th>
                      <th className="text-left font-semibold px-4 py-3">Email</th>
                      <th className="text-right font-semibold px-4 py-3">Entries</th>
                      <th className="text-right font-semibold px-4 py-3">Hours</th>
                      <th className="text-right font-semibold px-4 py-3">Cost</th>
                      <th className="text-right font-semibold px-4 py-3"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {freelancerOptions.map((r, idx) => {
                      const pct =
                        grandTotal > 0 ? Math.round((r.total_hours / grandTotal) * 100) : 0;
                      const isTop = idx === 0 && r.total_hours > 0;
                      return (
                        <tr
                          key={r.user_id}
                          className="border-t border-slate-100 hover:bg-slate-50/50 transition"
                          data-testid={`admin-totals-row-${r.user_id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {isTop && (
                                <Crown
                                  size={16}
                                  fill={BRAND.purple}
                                  style={{ color: BRAND.purple }}
                                  data-testid={`top-performer-crown-${r.user_id}`}
                                />
                              )}
                              <span className="font-medium text-slate-800">{r.name}</span>
                              {!r.is_active && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">
                                  inactive
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-500">{r.email}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {r.entry_count}
                          </td>
                          <td
                            className="px-4 py-3 text-right font-semibold tabular-nums"
                            style={{ color: BRAND.teal }}
                          >
                            {formatHours(r.total_hours)}
                            <div className="mt-1 h-1.5 w-24 ml-auto bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${pct}%`,
                                  background: `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.purple})`,
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {r.cost != null ? (
                              <span className="text-slate-800 font-medium">
                                ${Number(r.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            ) : (
                              <span
                                className="text-slate-400 text-xs"
                                title="Set this freelancer's hourly rate on the Team page"
                              >
                                —
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={() =>
                                  navigate(
                                    `/dashboard?as=${r.user_id}&as_name=${encodeURIComponent(r.name)}`
                                  )
                                }
                                data-testid={`view-as-${r.user_id}`}
                                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition"
                                title={`Open ${r.name}'s dashboard (read-only)`}
                              >
                                <Eye size={12} />
                                View as
                              </button>
                              <button
                                onClick={() => setUserFilter(r.user_id)}
                                data-testid={`filter-by-${r.user_id}`}
                                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition"
                              >
                                View entries <ChevronRight size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* By project */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              By project
            </h3>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {loading && !projectTotals ? (
              <div className="p-10 flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-400" size={20} />
              </div>
            ) : !projectTotals?.rows?.length ? (
              <div className="p-10 text-center text-sm text-slate-500">
                <FolderOpen
                  className="mx-auto mb-2 text-slate-300"
                  size={28}
                />
                No project hours in this period yet. Create projects in the
                Projects page so freelancers can categorize their entries.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left font-semibold px-4 py-3">Project</th>
                      <th className="text-left font-semibold px-4 py-3">Client</th>
                      <th className="text-right font-semibold px-4 py-3">Entries</th>
                      <th className="text-right font-semibold px-4 py-3">Hours</th>
                      <th className="text-right font-semibold px-4 py-3"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectTotals.rows.map((r) => {
                      const pct =
                        (projectTotals.grand_total_hours || 0) > 0
                          ? Math.round(
                              (r.total_hours / projectTotals.grand_total_hours) * 100
                            )
                          : 0;
                      return (
                        <tr
                          key={r.project_id || "_none_"}
                          className="border-t border-slate-100 hover:bg-slate-50/50 transition"
                        >
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-2">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ background: r.color || "#94a3b8" }}
                              />
                              <span className="font-medium text-slate-800">
                                {r.name}
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {r.client || (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {r.entry_count}
                          </td>
                          <td
                            className="px-4 py-3 text-right font-semibold tabular-nums"
                            style={{ color: BRAND.teal }}
                          >
                            {formatHours(r.total_hours)}
                            <div className="mt-1 h-1.5 w-24 ml-auto bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${pct}%`,
                                  background: r.color
                                    ? r.color
                                    : `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.purple})`,
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {r.project_id && (
                              <button
                                onClick={() => setProjectFilter(r.project_id)}
                                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition"
                              >
                                View entries <ChevronRight size={12} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Commissions review queue */}
        <div className="mt-8">
          <AdminCommissionsCard />
        </div>

        {/* All entries */}
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              {userFilter || projectFilter ? "Entries (filtered)" : "All entries"}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-500 font-medium">Freelancer:</label>
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                data-testid="admin-user-filter"
                className="text-sm px-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 bg-white"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                <option value="">All</option>
                {freelancerOptions.map((r) => (
                  <option key={r.user_id} value={r.user_id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <label className="text-xs text-slate-500 font-medium">Project:</label>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                data-testid="admin-project-filter"
                className="text-sm px-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 bg-white"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                <option value="">All</option>
                {allProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.client ? ` · ${p.client}` : ""}
                  </option>
                ))}
              </select>
              <label className="text-xs text-slate-500 font-medium">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="admin-status-filter"
                className="text-sm px-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 bg-white"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
              </select>
              {(userFilter || projectFilter || statusFilter) && (
                <button
                  onClick={() => {
                    setUserFilter("");
                    setProjectFilter("");
                    setStatusFilter("");
                  }}
                  data-testid="admin-filter-clear"
                  className="text-xs text-slate-500 hover:text-slate-900 underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {loading && !entriesData ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-slate-400" size={20} />
            </div>
          ) : error ? (
            <div
              className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm"
              data-testid="admin-error"
            >
              {error}
            </div>
          ) : (
            <AdminEntriesReadonly
              entriesData={entriesData}
              onPageChange={setEntriesPage}
              onApprove={handleApprove}
              onReturn={handleReturn}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// Read-only entries table for admin (no edit/delete, shows freelancer name)
function AdminEntriesReadonly({ entriesData, onPageChange, onApprove, onReturn }) {
  const entries = entriesData?.entries || [];
  if (!entries || entries.length === 0) {
    return (
      <div
        className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center"
        data-testid="admin-entries-empty"
      >
        <h4 className="text-sm font-semibold text-slate-700">No entries in this period</h4>
        <p className="mt-1 text-xs text-slate-500">
          Try a different date range or freelancer filter.
        </p>
      </div>
    );
  }
  return (
    <>
      <EntriesTableReadonly
        entries={entries}
        onApprove={onApprove}
        onReturn={onReturn}
      />
      <PaginationControls
        page={entriesData?.page || 1}
        totalPages={entriesData?.total_pages || 0}
        totalCount={entriesData?.total_count || entriesData?.entry_count || 0}
        pageSize={entriesData?.page_size || 50}
        onPageChange={onPageChange}
      />
    </>
  );
}

function PaginationControls({ page, totalPages, totalCount, pageSize, onPageChange }) {
  if (!totalPages || totalPages <= 1) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalCount);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <div className="text-slate-500">
        Showing {first}-{last} of {totalCount}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(page - 1, 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
        >
          <ChevronLeft size={14} />
          Previous
        </button>
        <span className="text-slate-500 tabular-nums">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(page + 1, totalPages))}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function EntriesTableReadonly({ entries, onApprove, onReturn }) {
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      data-testid="admin-entries-table"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <th className="text-left font-semibold px-4 py-3">Date</th>
              <th className="text-left font-semibold px-4 py-3">Freelancer</th>
              <th className="text-left font-semibold px-4 py-3">Project</th>
              <th className="text-left font-semibold px-4 py-3">Activity</th>
              <th className="text-right font-semibold px-4 py-3">Hours</th>
              <th className="text-center font-semibold px-4 py-3">Source</th>
              <th className="text-center font-semibold px-4 py-3">Status</th>
              <th className="text-right font-semibold px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <AdminEntryRow
                key={e.id}
                entry={e}
                onApprove={onApprove}
                onReturn={onReturn}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminEntryRow({ entry, onApprove, onReturn }) {
  const status = entryStatus(entry);
  return (
    <tr
      className="border-t border-slate-100 hover:bg-slate-50/50 transition"
      data-testid={`admin-entry-row-${entry.id}`}
    >
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
        {formatFriendlyDate(entry.date)}
      </td>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-medium">
        {entry.user_name || "Unknown"}
      </td>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
        {entry.project_name ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              background: `${entry.project_color || "#94a3b8"}20`,
              color: entry.project_color || "#475569",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: entry.project_color || "#94a3b8" }}
            />
            {entry.project_name}
          </span>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-800">{entry.activity}</td>
      <td
        className="px-4 py-3 text-right font-semibold tabular-nums"
        style={{ color: BRAND.teal }}
      >
        {formatHours(entry.hours)}
      </td>
      <td className="px-4 py-3 text-center">
        <span
          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{
            background:
              entry.source === "timer" ? `${BRAND.teal}15` : `${BRAND.purple}15`,
            color: entry.source === "timer" ? BRAND.teal : BRAND.purple,
          }}
        >
          {entry.source}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <StatusBadge entry={entry} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {status === "submitted" && (
            <>
              <button
                onClick={() => onApprove?.(entry)}
                data-testid={`approve-entry-${entry.id}`}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border transition"
                style={{
                  color: BRAND.tealDark,
                  borderColor: `${BRAND.teal}66`,
                  background: `${BRAND.teal}15`,
                }}
                title="Approve"
              >
                <Check size={12} />
                Approve
              </button>
              <button
                onClick={() => onReturn?.(entry)}
                data-testid={`return-entry-${entry.id}`}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border text-slate-600 border-slate-200 hover:bg-slate-50 transition"
                title="Return to draft for edits"
              >
                <RotateCcw size={12} />
                Return
              </button>
            </>
          )}
          {status === "approved" && (
            <button
              onClick={() => onReturn?.(entry)}
              data-testid={`return-entry-${entry.id}`}
              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border text-slate-600 border-slate-200 hover:bg-slate-50 transition"
              title="Unapprove and send back to draft"
            >
              <RotateCcw size={12} />
              Unapprove
            </button>
          )}
          {status === "draft" && (
            <span className="text-xs text-slate-400">—</span>
          )}
        </div>
      </td>
    </tr>
  );
}
